import {
  BriefFitErrorCodes,
  BriefSocialMediaAiFitCheckStatus,
  CreatorProfileJobStatus,
  IBriefFitEvaluation,
  IBriefInfluencerV2,
} from "@shared/lib/types/briefInfluencerV2";

export type AiFitAnalysis = NonNullable<
  NonNullable<IBriefInfluencerV2["briefFitEvaluationJob"]>["aiAnalysis"]
>;

export type AiRecommendation =
  | "shortlist"
  | "consider_with_caution"
  | "manual_review"
  | "reject";

interface RecommendationDisplay {
  label: string;
  color: string;
  bg: string;
}

const RECOMMENDATION_CONFIG: Record<AiRecommendation, RecommendationDisplay> = {
  shortlist: { label: "SHORTLIST", color: "#0a8f54", bg: "#e6f9f0" },
  consider_with_caution: {
    label: "CONSIDER",
    color: "#b45309",
    bg: "#fef3c7",
  },
  manual_review: { label: "MANUAL REVIEW", color: "#635bff", bg: "#eef0ff" },
  reject: { label: "REJECT", color: "#df1b41", bg: "#fdecef" },
};

const FALLBACK_DISPLAY: RecommendationDisplay = {
  label: "ANALYZED",
  color: "#697386",
  bg: "#f1f5f9",
};

function normalizeRecommendation(
  recommendation?: string
): AiRecommendation | undefined {
  if (!recommendation) return undefined;
  const key = recommendation
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (key in RECOMMENDATION_CONFIG) {
    return key as AiRecommendation;
  }
  return undefined;
}

export function getRecommendationDisplay(
  recommendation?: string,
  verdict?: string
): RecommendationDisplay {
  const normalized = normalizeRecommendation(recommendation);
  if (normalized) {
    return RECOMMENDATION_CONFIG[normalized];
  }
  if (verdict) {
    return { ...FALLBACK_DISPLAY, label: verdict.toUpperCase() };
  }
  return FALLBACK_DISPLAY;
}

export type ScoreTier =
  | "very_poor"
  | "weak"
  | "partial"
  | "good"
  | "strong"
  | "excellent";

interface ScoreTierDisplay {
  color: string;
  heroClass: `aiHero_${ScoreTier}`;
}

const SCORE_TIER_CONFIG: Record<ScoreTier, ScoreTierDisplay> = {
  very_poor: {
    color: "#df1b41",
    heroClass: "aiHero_very_poor",
  },
  weak: {
    color: "#e04f16",
    heroClass: "aiHero_weak",
  },
  partial: {
    color: "#b45309",
    heroClass: "aiHero_partial",
  },
  good: {
    color: "#65a30d",
    heroClass: "aiHero_good",
  },
  strong: {
    color: "#16a34a",
    heroClass: "aiHero_strong",
  },
  excellent: {
    color: "#0a8f54",
    heroClass: "aiHero_excellent",
  },
};

const SCORE_FALLBACK_DISPLAY: ScoreTierDisplay = {
  color: "#697386",
  heroClass: "aiHero_very_poor",
};

export function getScoreTier(score?: number): ScoreTier | undefined {
  if (score === undefined || score === null) return undefined;
  if (score <= 20) return "very_poor";
  if (score <= 40) return "weak";
  if (score <= 60) return "partial";
  if (score <= 75) return "good";
  if (score <= 90) return "strong";
  return "excellent";
}

export function getScoreDisplay(score?: number): ScoreTierDisplay {
  const tier = getScoreTier(score);
  if (!tier) return SCORE_FALLBACK_DISPLAY;
  return SCORE_TIER_CONFIG[tier];
}

export function getScoreColor(score?: number): string {
  return getScoreDisplay(score).color;
}

type StatusTone = "success" | "warning" | "danger";

export function getRiskTone(level?: string): StatusTone {
  const l = level?.trim().toLowerCase();
  if (l === "low") return "success";
  if (l === "medium") return "warning";
  return "danger";
}

export function getAiFitCheckSortScore(
  record: IBriefInfluencerV2
): number | null {
  const fitCheck = record.briefFitEvaluationJob;
  if (!fitCheck) return null;

  const { status } = fitCheck;
  if (
    !status ||
    status === BriefSocialMediaAiFitCheckStatus.Evaluating ||
    status === BriefSocialMediaAiFitCheckStatus.WaitingCreatorProfile ||
    status === BriefSocialMediaAiFitCheckStatus.WaitingPrerequisites ||
    status === BriefSocialMediaAiFitCheckStatus.Failed
  ) {
    return null;
  }

  const score = fitCheck.aiAnalysis?.creator_campaign_fit?.overall_score;
  if (score === undefined || score === null) return null;
  return score;
}

export function compareAiFitCheckByScore(
  a: IBriefInfluencerV2,
  b: IBriefInfluencerV2,
  sortOrder?: "ascend" | "descend" | null
): number {
  const aScore = getAiFitCheckSortScore(a);
  const bScore = getAiFitCheckSortScore(b);

  if (aScore === null && bScore === null) return 0;
  if (aScore === null) return sortOrder === "descend" ? -1 : 1;
  if (bScore === null) return sortOrder === "descend" ? 1 : -1;

  return aScore - bScore;
}

export const profileJobErrorToText: Record<string, string> = {
  BASIC_INSIGHTS_NOT_ENABLED: "Basic Insights Not Enabled",
  BASIC_INSIGHTS_FAILED: "Basic Insights Failed",
  COLLECT_MEDIA_FAILED: "Media Collection Failed",
  PLATFORM_NOT_SUPPORTED: "Platform Not Supported",
  USER_NOT_FOUND: "User Not Found",
  USER_NO_CONTENT: "User Content Not Found",
  USER_INSUFFICIENT_CONTENT: "User Content Insufficient",
  NO_MEDIAS: "No Medias",
  INSUFFICIENT_DOWNLOADS: "Insufficient Downloads",
  AGGREGATION_FAILED: "AI Pipeline Failed",
  PIPELINE_ERROR: "Pipeline Failed",
};


export function getFailureReason(fitCheck: IBriefFitEvaluation) {
  if (fitCheck?.failure?.code === BriefFitErrorCodes.CreatorProfileJobFailed) {
    return fitCheck?.creatorProfileJob?.failure?.code ? profileJobErrorToText[fitCheck?.creatorProfileJob?.failure?.code] : "Something went wrong.";
  }
  else {
    return fitCheck?.failure?.message || "Something went wrong.";
  }
}

export const checkFitCheckRetry = (fitCheck: IBriefFitEvaluation) => {
  switch (fitCheck?.failure?.code) {
    case BriefFitErrorCodes.BriefNotFound:
      return true;
    case BriefFitErrorCodes.CreatorProfileJobFailed:
      switch (fitCheck?.creatorProfileJob?.failure?.code) {
        case CreatorProfileJobStatus.BasicInsightsNotEnabled:
          return false;
        case CreatorProfileJobStatus.BasicInsightsFailed:
          return false;
        case CreatorProfileJobStatus.CollectMediaFailed:
          return true;
        case CreatorProfileJobStatus.PlatformNotSupported:
          return true;
        case CreatorProfileJobStatus.UserNotFound:
          return false;
        case CreatorProfileJobStatus.UserNoContent:
          return false;
        case CreatorProfileJobStatus.UserInsufficientContent:
          return true;
        case CreatorProfileJobStatus.NoMedias:
          return true;
        case CreatorProfileJobStatus.InsufficientDownloads:
          return true;
        case CreatorProfileJobStatus.AggregationFailed:
          return true;
        case CreatorProfileJobStatus.PipelineError:
          return true;
        default:
          return true;
      }
    case BriefFitErrorCodes.FitCheckDisabled:
      return true;
    case BriefFitErrorCodes.PlatformNotSupported:
      return true;
    case BriefFitErrorCodes.BasicDataFailed:
      return false;
    case BriefFitErrorCodes.AudienceDataFailed:
      return false;
    case BriefFitErrorCodes.CreatorProfileFailed:
      return true;
    case BriefFitErrorCodes.EvaluationFailed:
      return true;
    default:
      return true;
  }
}

export const checkCreatorProfileFitCheckRetry = (code?: CreatorProfileJobStatus) => {
  switch (code) {
    case CreatorProfileJobStatus.BasicInsightsNotEnabled:
      return false;
    case CreatorProfileJobStatus.PlatformNotSupported:
      return true;
    case CreatorProfileJobStatus.UserNotFound:
      return false;
    case CreatorProfileJobStatus.UserNoContent:
      return false;
    case CreatorProfileJobStatus.UserInsufficientContent:
      return true;
    case CreatorProfileJobStatus.NoMedias:
      return true;
    case CreatorProfileJobStatus.InsufficientDownloads:
      return true;
    case CreatorProfileJobStatus.AggregationFailed:
      return true;
    case CreatorProfileJobStatus.PipelineError:
      return true;
    default:
      return true;
  }
}